import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Link,
  Divider
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';

const Contact = () => {
  // Form state
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    options: {
      resonateWithContent: false,
      lightWorker: false,
      collaborate: false
    },
    message: '',
    nameError: '',
    emailError: '',
    phoneError: '',
    messageError: '',
    isLoading: false,
    isError: false,
    isSuccess: false
  });
  
  // Estado para determinar el dominio actual
  const [currentDomain, setCurrentDomain] = useState('');
  
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.endsWith('.cl')) {
      setCurrentDomain('cl');
    } else if (hostname.endsWith('.es')) {
      setCurrentDomain('es');
    } else {
      setCurrentDomain('cl');
    }
  }, []);

  // Scroll to top when form is successfully submitted
  useEffect(() => {
    if (formState.isSuccess) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [formState.isSuccess]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value,
      [`${name}Error`]: '' // Clear error when user types
    }));
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormState(prevState => ({
      ...prevState,
      options: {
        ...prevState.options,
        [name]: checked
      }
    }));
  };

  // Form validation
  const validate = () => {
    let nameError = '';
    let emailError = '';
    let phoneError = '';
    let messageError = '';
    let hasError = false;

    // Name validation
    if (!formState.name.trim()) {
      nameError = 'Por favor, ingresa tu nombre';
      hasError = true;
    }

    // Email validation
    if (!formState.email.trim()) {
      emailError = 'Por favor, ingresa tu correo electrónico';
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      emailError = 'Por favor, ingresa un correo electrónico válido';
      hasError = true;
    }

    // Phone validation (optional but validate format if provided)
    if (formState.phone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(formState.phone)) {
      phoneError = 'Por favor, ingresa un número de teléfono válido, con código de país';
      hasError = true;
    }

    // Message validation
    if (!formState.message.trim()) {
      messageError = 'Por favor, ingresa un mensaje';
      hasError = true;
    }

    // Update state with validation errors
    setFormState(prevState => ({
      ...prevState,
      nameError,
      emailError,
      phoneError,
      messageError
    }));

    return { hasError };
  };

  // API call function
  const contact = async (data) => {
    try {
      const response = await fetch("https://api.labs.jeshua.ong/contact", {
        method: "POST",
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/json" },
      });
      return await response.json();
    } catch (error) {
      console.error("Error sending contact form:", error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    const { hasError } = validate();

    if (hasError) {
      return;
    }

    setFormState(prevState => ({
      ...prevState,
      isLoading: true,
      isError: false,
      isSuccess: false
    }));

    try {
      const response = await contact({
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        options: formState.options,
        message: formState.message
      });

      if (response && response.code === 0) {
        setFormState(prevState => ({
          ...prevState,
          name: '',
          email: '',
          phone: '',
          options: {
            resonateWithContent: false,
            lightWorker: false,
            collaborate: false
          },
          message: '',
          isLoading: false,
          isError: false,
          isSuccess: true
        }));
      } else {
        setFormState(prevState => ({
          ...prevState,
          isLoading: false,
          isError: true,
          isSuccess: false
        }));
      }
    } catch (e) {
      console.error('Contact form submission error:', e);
      setFormState(prevState => ({
        ...prevState,
        isLoading: false,
        isError: true,
        isSuccess: false
      }));
    }
  };

  // Configuración de los datos de contacto según el dominio
  const contactDetails = {
    cl: {
      whatsappLabel: "WhatsApp:",
      whatsappNumber: "+56 9 6822 4080",
      whatsappUrl: "https://wa.me/56968224080"
    },
    es: {
      whatsappLabel: "WhatsApp:",
      whatsappNumber: "+34 617 59 84 63",
      whatsappUrl: "https://wa.me/34617598463"
    }
  };

  // Obtener los detalles correctos según el dominio actual
  const currentContact = contactDetails[currentDomain] || contactDetails.cl;

  return (
    <Box>
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Contact Information */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            bgcolor: '#ff5722',
            color: '#000',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Grid container sx={{ textAlign: 'center' }}>
                <Grid item xs={12}>
                  <EmailIcon sx={{ color: '#424a4a', fontSize: '3em' }} />
                </Grid>
                <Grid item xs={12} sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Email:
                </Grid>
                <Grid item xs={12}>
                  <Link 
                    href="mailto:contacto@jeshua.ong" 
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      fontSize: '1.2em',
                      '&:hover': {
                        color: '#ddd',
                        textDecoration: 'underline'
                      } 
                    }}
                  >
                    contacto@jeshua.ong
                  </Link>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Grid container sx={{ textAlign: 'center' }}>
                <Grid item xs={12}>
                  <WhatsAppIcon sx={{ color: '#424a4a', fontSize: '3em' }} />
                </Grid>
                <Grid item xs={12} sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {currentContact.whatsappLabel}
                </Grid>
                <Grid item xs={12}>
                  <Link 
                    href={currentContact.whatsappUrl} 
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      fontSize: '1.2em',
                      '&:hover': {
                        color: '#ddd',
                        textDecoration: 'underline'
                      } 
                    }}
                  >
                    {currentContact.whatsappNumber}
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Contact Form */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            bgcolor: '#fff',
            borderRadius: 2,
          }}
        >

          {formState.isSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Tu mensaje ha sido enviado correctamente. ¡Gracias por contactarnos!
            </Alert>
          )}

          {formState.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Ha ocurrido un error al enviar tu mensaje. Por favor, inténtalo de nuevo más tarde.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  error={!!formState.nameError}
                  helperText={formState.nameError}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={formState.email}
                  onChange={handleChange}
                  error={!!formState.emailError}
                  helperText={formState.emailError}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  error={!!formState.phoneError}
                  helperText={formState.phoneError}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="textSecondary">
                  Selecciona las opciones que te representen:
                </Typography>
                <Box>
                  {/* Primera opción - Versión móvil */}
                  <Box sx={{ 
                    display: { xs: 'block', sm: 'none' } // Solo visible en móvil
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.resonateWithContent} 
                          onChange={handleCheckboxChange} 
                          name="resonateWithContent"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            },
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            transform: 'translateY(-3px)' // Ajuste fino para alinear mejor el checkbox
                          }}
                        />
                      }
                      label="Me hacen sentido las palabras y/o la música escuchada en Jeshua."
                      sx={{
                        alignItems: 'flex-start',
                        margin: 0,
                        '.MuiFormControlLabel-label': {
                          paddingTop: '2px' // Ajuste fino para el texto
                        },
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                    <Divider sx={{ my: 1 }} />
                  </Box>

                  {/* Versión desktop de la primera opción */}
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'block' } // Solo visible en desktop
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.resonateWithContent} 
                          onChange={handleCheckboxChange} 
                          name="resonateWithContent"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            }
                          }}
                        />
                      }
                      label="Me hacen sentido las palabras y/o la música escuchada en Jeshua."
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Box>

                  {/* Segunda opción - Versión móvil */}
                  <Box sx={{ 
                    display: { xs: 'block', sm: 'none' } // Solo visible en móvil
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.lightWorker} 
                          onChange={handleCheckboxChange} 
                          name="lightWorker"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            },
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            transform: 'translateY(-3px)' // Ajuste fino para alinear mejor el checkbox
                          }}
                        />
                      }
                      label="Me siento un(a) Trabajador(a) de la Luz, Sanador(a), Canalizador(a) y/o Músico."
                      sx={{
                        alignItems: 'flex-start',
                        margin: 0,
                        '.MuiFormControlLabel-label': {
                          paddingTop: '2px' // Ajuste fino para el texto
                        },
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                    <Divider sx={{ my: 1 }} />
                  </Box>

                  {/* Versión desktop de la segunda opción */}
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'block' } // Solo visible en desktop
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.lightWorker} 
                          onChange={handleCheckboxChange} 
                          name="lightWorker"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            }
                          }}
                        />
                      }
                      label="Me siento un(a) Trabajador(a) de la Luz, Sanador(a), Canalizador(a) y/o Músico."
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Box>

                  {/* Tercera opción - Versión móvil */}
                  <Box sx={{ 
                    display: { xs: 'block', sm: 'none' } // Solo visible en móvil
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.collaborate} 
                          onChange={handleCheckboxChange} 
                          name="collaborate"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            },
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            transform: 'translateY(-3px)' // Ajuste fino para alinear mejor el checkbox
                          }}
                        />
                      }
                      label="Me gustaría colaborar en la próxima etapa de la plataforma Jeshua."
                      sx={{
                        alignItems: 'flex-start',
                        margin: 0,
                        '.MuiFormControlLabel-label': {
                          paddingTop: '2px' // Ajuste fino para el texto
                        },
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Box>

                  {/* Versión desktop de la tercera opción */}
                  <Box sx={{ 
                    display: { xs: 'none', sm: 'block' } // Solo visible en desktop
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formState.options.collaborate} 
                          onChange={handleCheckboxChange} 
                          name="collaborate"
                          sx={{ 
                            color: '#424a4a',
                            '&.Mui-checked': {
                              color: '#ff5722',
                            }
                          }}
                        />
                      }
                      label="Me gustaría colaborar en la próxima etapa de la plataforma Jeshua."
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mensaje"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  error={!!formState.messageError}
                  helperText={formState.messageError}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={formState.isLoading}
                  endIcon={formState.isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{
                    mt: 2,
                    bgcolor: '#ff5722',
                    '&:hover': {
                      bgcolor: '#e64a19',
                    },
                  }}
                >
                  {formState.isLoading ? 'Enviando...' : 'Enviar Mensaje'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Contact;